/**
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * 📱 TABLE VIEW (SCORING DISPLAY)
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * 
 * Tisch-Ansicht für dedizierte Scoring-Displays:
 * - Zeigt zugewiesenes Spiel an
 * - Ermöglicht Scoring (wie normale GameDetail Page)
 * - Sendet regelmäßig Heartbeats
 * - Zeigt Status (pending/approved/waiting for game)
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getTableDeviceByUuid,
  sendHeartbeat,
  isTableOnline,
} from '../services/tableDevices';
import type { TableDevice } from '../types';
import './TableView.css';

const DEVICE_UUID_KEY = 'snooker_table_device_uuid';
const HEARTBEAT_INTERVAL = 30000; // 30 seconds
const POLL_INTERVAL = 10000; // 10 seconds (check for game assignment)

export const TableView: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [device, setDevice] = useState<TableDevice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ============================================================================
  // DEVICE LOADING
  // ============================================================================

  const loadDevice = useCallback(async () => {
    const deviceUuid = localStorage.getItem(DEVICE_UUID_KEY);

    if (!deviceUuid) {
      // Not registered, redirect to registration
      navigate('/table-registration');
      return;
    }

    try {
      const deviceData = await getTableDeviceByUuid(deviceUuid);

      if (!deviceData) {
        // Device was deleted, clear localStorage and redirect
        localStorage.removeItem(DEVICE_UUID_KEY);
        navigate('/table-registration');
        return;
      }

      setDevice(deviceData);
      setError(null);
    } catch (err: any) {
      console.error('Error loading device:', err);
      setError(err.message || 'Failed to load table device');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // ============================================================================
  // INITIAL LOAD
  // ============================================================================

  useEffect(() => {
    loadDevice();
  }, [loadDevice]);

  // ============================================================================
  // HEARTBEAT
  // ============================================================================

  useEffect(() => {
    const deviceUuid = localStorage.getItem(DEVICE_UUID_KEY);
    if (!deviceUuid) return;

    const sendHeartbeatPing = async () => {
      try {
        await sendHeartbeat(deviceUuid, false);
      } catch (err) {
        console.error('Heartbeat failed:', err);
      }
    };

    // Send initial heartbeat
    sendHeartbeatPing();

    // Set up interval
    const interval = setInterval(sendHeartbeatPing, HEARTBEAT_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  // ============================================================================
  // POLLING FOR GAME ASSIGNMENT
  // ============================================================================

  useEffect(() => {
    if (!device || device.status !== 'approved' || device.current_game_id) {
      return; // Only poll if approved and no game assigned yet
    }

    const poll = setInterval(() => {
      loadDevice(); // Reload to check for game assignment
    }, POLL_INTERVAL);

    return () => clearInterval(poll);
  }, [device, loadDevice]);

  // ============================================================================
  // RENDERING
  // ============================================================================

  if (loading) {
    return (
      <div className="table-view-container">
        <div className="status-card">
          <div className="loading-spinner"></div>
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !device) {
    return (
      <div className="table-view-container">
        <div className="status-card error-card">
          <h2>⚠️ {t('common.error')}</h2>
          <p>{error || 'Device not found'}</p>
          <button onClick={() => navigate('/table-registration')} className="btn-primary">
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  // ──────────────────────────────────────────────────────────────────────────
  // STATUS: PENDING (waiting for approval)
  // ──────────────────────────────────────────────────────────────────────────
  if (device.status === 'pending') {
    return (
      <div className="table-view-container">
        <div className="status-card pending-card">
          <div className="status-icon">⏳</div>
          <h2>{t('tableDevices.status.pending')}</h2>
          <p className="table-name">{device.table_name}</p>
          {device.location && <p className="location">📍 {device.location}</p>}
          <p className="status-message">
            {t('tableDevices.registration.waitingForApproval')}
          </p>
          <div className="pulsing-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
    );
  }

  // ──────────────────────────────────────────────────────────────────────────
  // STATUS: DISABLED
  // ──────────────────────────────────────────────────────────────────────────
  if (device.status === 'disabled') {
    return (
      <div className="table-view-container">
        <div className="status-card disabled-card">
          <div className="status-icon">🚫</div>
          <h2>{t('tableDevices.status.disabled')}</h2>
          <p className="table-name">{device.table_name}</p>
          <p className="status-message">{t('tableDevices.view.contactAdmin')}</p>
        </div>
      </div>
    );
  }

  // ──────────────────────────────────────────────────────────────────────────
  // STATUS: APPROVED, but NO GAME assigned
  // ──────────────────────────────────────────────────────────────────────────
  if (!device.current_game_id) {
    return (
      <div className="table-view-container">
        <div className="status-card ready-card">
          <div className="status-icon">✅</div>
          <h2>{device.table_name}</h2>
          {device.location && <p className="location">📍 {device.location}</p>}
          <div className="status-badge approved">
            {t('tableDevices.status.approved')}
          </div>
          <p className="status-message">
            {t('tableDevices.view.waitingForGame')}
          </p>
          <div className="pulsing-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
    );
  }

  // ──────────────────────────────────────────────────────────────────────────
  // STATUS: APPROVED + GAME assigned → Redirect to GamePlay in Table Mode
  // ──────────────────────────────────────────────────────────────────────────
  // We'll redirect to /play/:id with table mode enabled
  // The RLS policies ensure the table can only see/update THIS game.
  
  return (
    <div className="table-view-container">
      <div className="status-card game-assigned-card">
        <div className="status-icon">🎯</div>
        <h2>{device.table_name}</h2>
        <p className="status-message">{t('tableDevices.view.gameAssigned')}</p>
        <p className="game-id">Game #{device.current_game_id}</p>
        <button
          onClick={() => {
            // Set table mode flag
            sessionStorage.setItem('table_mode', 'true');
            // Redirect to game play page
            navigate(`/play/${device.current_game_id}`);
          }}
          className="btn-start-game"
        >
          {t('common.play')} 🎱
        </button>
      </div>
    </div>
  );
};

